import { useDispatch, useSelector } from 'react-redux';
import Button from "components/molecules/Button";
import ReducersIndex from 'reduxModules/ducks';
import { useContext, useEffect } from 'react';
import { some } from 'lodash';
import ToolContext from './ToolContext';

/**
* @module Tutorial
* @description A React component that manages tutorial initialization, state,
* and cleanup for the application's guided tour functionality.
* 
* @component
* @description
* Manages tutorial workflow by:
* 1. Tutorial State Control:
*    - Initializes tutorial configuration
*    - Handles modal visibility
*    - Manages spotlight targeting
*    - Controls reset states
* 
* 2. Cleanup Management:
*    - Clears spotlight targets
*    - Resets tutorial changes
*    - Cleans up tutorial state
*    - Handles modal closure
* 
* 3. Context Integration:
*    - Accesses tutorial steps
*    - Uses tool-specific context
*    - Maintains configuration state
*    - Handles tool-specific resets
* 
* @example
* // Basic usage
* <Tutorial />
* 
* // Required context structure
* const toolContext = {
*   tutorialSteps: [
*     {
*       content: "Step 1 content",
*       target: "menuItem1",
*       action: "highlightMenu"
*     }
*   ]
* };
* 
* @note
* State Management:
* - Uses Redux for global state
* - Tracks tutorial modal state
* - Manages spotlight targets
* - Handles reset states
* 
* Cleanup Handling:
* - Removes spotlight on close
* - Resets tutorial changes
* - Clears configuration
* - Manages state cleanup
* 
* Integration Points:
* - Uses ToolContext
* - Dispatches Redux actions
* - Controls modal state
* - Manages tutorial flow
* 
* Dependencies:
* - Requires Redux store
* - Needs ToolContext
* - Uses Button component
* - Relies on tutorial configuration
*/
const Tutorial = (props) => {
  const isTutorialOpen = useSelector(state => state.ui.isModalOpen && some(state.ui.modals, modal => modal.modalName === 'tutorial' && modal.modalOpen));
  const dispatch = useDispatch();
  const context = useContext(ToolContext);
  const tutorialSteps = context.tutorialSteps;

  const tutorialReset = useSelector(state => state.ui.tutorialReset);
  // Clear spotlight
  useEffect(() => {
    if (!isTutorialOpen) {
      dispatch(ReducersIndex.uiReducers.setSpotlightTarget(null));
      dispatch(ReducersIndex.container.resetTutorialChanges(tutorialReset));
      dispatch(ReducersIndex.uiReducers.setTutorialReset(null));
    }
  }, [isTutorialOpen]);

  const handleClick = () => {
    dispatch(ReducersIndex.uiReducers.setTutorialConfig(tutorialSteps));
    dispatch(ReducersIndex.uiReducers.openModal('tutorial'));
  };

  return (
    <Button name={'help'} state={'button_active'} onClick={handleClick} />
  )
};

export default Tutorial;